import { useCallback, useEffect, useRef, useState } from 'react'

// Auto-save form drafts to localStorage every N ms so a browser crash / tab
// close / power loss doesn't wipe 20 minutes of typing.  Restore surfaced via
// a discoverable banner ("You have unsaved changes from 5 minutes ago") rather
// than silent restore, so users know what happened.
//
// Usage:
//   const draft = useFormDraft('lextrack_draft_matter_new', form, setForm, {
//     enabled: !editingMatter,   // only auto-save on the create path
//     debounceMs: 3000,
//     maxAgeMs: 24 * 60 * 60 * 1000,
//   })
//
//   // On successful submit:
//   draft.clear()
//
// Then render draft.banner somewhere at the top of the form:
//   {draft.banner}
//
// The banner appears once on mount if a fresh draft is found; the user picks
// Restore or Discard; the banner then goes away.
//
// Design:
//   - Snapshot writes are throttled with a trailing-edge debounce so rapid
//     keystrokes don't hammer localStorage.
//   - "Fresh" state (form matches the initial state) is NOT saved — otherwise
//     an empty form gets persisted as a bogus draft that pops the banner next
//     time the user opens the form.

const isPrimitiveEqual = (a, b) => {
  if (a === b) return true
  if (typeof a !== typeof b) return false
  if (typeof a === 'object' && a !== null && b !== null) {
    // Shallow enough for form state — arrays + flat objects
    try { return JSON.stringify(a) === JSON.stringify(b) } catch { return false }
  }
  return false
}

export function useFormDraft(storageKey, form, setForm, opts = {}) {
  const {
    enabled = true,
    debounceMs = 3000,
    maxAgeMs = 24 * 60 * 60 * 1000,   // 24 hours
    initialForm = null,               // used to detect "fresh" state
  } = opts

  const timerRef = useRef(null)
  const initialSnapshotRef = useRef(null)
  const [existingDraft, setExistingDraft] = useState(null)   // {form, savedAt}
  const [banner, setBanner] = useState(null)                 // React node

  // Read draft on mount + capture the initial form for later "isFresh" checks.
  // Runs once — deps intentionally empty.  Subsequent restores happen only via
  // user action on the banner.
  useEffect(() => {
    if (!enabled) return
    initialSnapshotRef.current = initialForm ?? form
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return
      const age = Date.now() - (parsed.savedAt || 0)
      if (age > maxAgeMs) { localStorage.removeItem(storageKey); return }
      // Ignore drafts that match the initial state (nothing meaningful to
      // restore — probably a stale write from last session's cleanup).
      if (isPrimitiveEqual(parsed.form, initialSnapshotRef.current)) {
        localStorage.removeItem(storageKey); return
      }
      setExistingDraft(parsed)
    } catch { /* corrupt draft — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-save with trailing-edge debounce.  Skips writes when the form
  // matches the initial state so we don't persist bogus "empty" drafts.
  useEffect(() => {
    if (!enabled) return
    if (isPrimitiveEqual(form, initialSnapshotRef.current)) return
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          form,
          savedAt: Date.now()
        }))
      } catch { /* localStorage full or blocked — non-fatal */ }
    }, debounceMs)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [form, storageKey, debounceMs, enabled])

  const clear = useCallback(() => {
    try { localStorage.removeItem(storageKey) } catch {}
    setExistingDraft(null)
    setBanner(null)
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [storageKey])

  const restore = useCallback(() => {
    if (!existingDraft) return
    try { setForm(existingDraft.form) } catch (e) { console.error(e) }
    setExistingDraft(null)
    setBanner(null)
  }, [existingDraft, setForm])

  const discard = useCallback(() => {
    try { localStorage.removeItem(storageKey) } catch {}
    setExistingDraft(null)
    setBanner(null)
  }, [storageKey])

  // Compose the banner element once a draft is detected.
  useEffect(() => {
    if (!existingDraft) { setBanner(null); return }
    const mins = Math.max(1, Math.round((Date.now() - existingDraft.savedAt) / 60000))
    const label = mins === 1 ? '1 minute ago' :
                  mins < 60 ? `${mins} minutes ago` :
                  mins < 120 ? 'about an hour ago' :
                  `${Math.round(mins / 60)} hours ago`
    // Build the JSX inline — no separate component file needed.
    setBanner(
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 flex items-start gap-3 text-sm">
        <span className="text-amber-500 shrink-0 text-lg leading-none mt-0.5">💾</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-amber-900">Unsaved draft from {label}</p>
          <p className="text-xs text-amber-800/80 mt-0.5">We saved your in-progress form when the tab closed.  Restore it, or discard and start fresh.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={restore}
            className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium">
            Restore
          </button>
          <button
            type="button"
            onClick={discard}
            className="text-xs px-3 py-1.5 text-amber-800 hover:text-amber-900 hover:underline">
            Discard
          </button>
        </div>
      </div>
    )
  }, [existingDraft, restore, discard])

  return { clear, banner, hasDraft: !!existingDraft }
}
